// Tiket antrian untuk printer thermal (58mm bawaan, 80mm opsional).
//
// MEKANISME CETAK — JANGAN DIUBAH TANPA UJI CETAK-KE-PDF.
// Komponen ini memakai `cetak()` milik components/faktur/FakturPrint.jsx apa
// adanya: ia MENYALIN `.print-area` ke `#print-root` yang jadi anak langsung
// <body>, lalu CSS `body.printing-doc > *:not(#print-root) { display:none }`
// menyembunyikan sisanya. Itulah perbaikan keluhan "faktur keluar 3-4 rangkap"
// (modal `position: fixed` diulang di setiap halaman kertas; `visibility:
// hidden` TIDAK cukup karena tetap menyisakan ruang). Lihat CLAUDE.md.
//
// Semua data datang dari props — komponen ini TIDAK pernah fetch sendiri
// (pola sama dengan StrukPrint.jsx).
import { useRef } from "react";
import { Printer } from "lucide-react";
import { cetak } from "../faktur/FakturPrint";
import { batangCode128 } from "./code128";

const LEBAR_MM = { 58: 58, 80: 80 };

/** Barcode CODE 128 sebagai SVG inline.
 *
 *  SVG, bukan <canvas>: kanvas ikut terbawa saat `cetak()` meng-clone node,
 *  TAPI isinya tidak — `cloneNode(true)` menyalin elemen kanvasnya saja, bukan
 *  piksel yang sudah digambar. Hasilnya tiket tercetak dengan kotak kosong di
 *  tempat barcode. SVG adalah DOM sungguhan, jadi ikut tersalin utuh.
 *
 *  `shape-rendering="crispEdges"` supaya batang tidak di-antialias jadi abu-abu
 *  tipis — printer thermal 203dpi menghasilkan batang yang gagal terbaca kalau
 *  tepinya kabur.
 */
function Barcode({ teks, tinggiMm = 14, lebarMm = 50 }) {
  const hasil = batangCode128(teks || "");
  if (!hasil) {
    // Kode yang tidak bisa disandikan JANGAN digambar asal — barcode yang
    // salah terbaca lebih berbahaya daripada tidak ada barcode. Kodenya tetap
    // tercetak sebagai teks, dan layar FL menerima ketikan manual.
    return (
      <p className="text-center text-[9px] text-slate-500" data-testid="tiket-barcode-gagal">
        (Barcode tidak bisa dibuat untuk kode ini — ketik kodenya manual di layar FL)
      </p>
    );
  }
  const { batang, totalModul } = hasil;
  const QUIET = 10; // zona sunyi wajib Code128: minimal 10 modul di kiri & kanan
  const total = totalModul + QUIET * 2;
  return (
    <svg
      data-testid="tiket-barcode"
      viewBox={`0 0 ${total} 100`}
      preserveAspectRatio="none"
      shapeRendering="crispEdges"
      role="img"
      aria-label={`Barcode ${teks}`}
      style={{ width: `${lebarMm}mm`, height: `${tinggiMm}mm`, display: "block", margin: "0 auto" }}
    >
      <rect x="0" y="0" width={total} height="100" fill="#ffffff" />
      {batang.map((b) => (
        <rect key={b.x} x={b.x + QUIET} y="0" width={b.lebar} height="100" fill="#000000" />
      ))}
    </svg>
  );
}

export default function TiketPrint({
  tiket,
  pengaturan = {},
  antreanDiDepan = null,
  tampilkanTombolCetak = true,
  onSelesaiCetak = null,
}) {
  const areaRef = useRef(null);
  if (!tiket) return null;

  const lebar = LEBAR_MM[Number(pengaturan.lebar_kertas_mm)] || 58;
  const judul = pengaturan.judul_tiket || "NOMOR ANTRIAN";
  const footer = pengaturan.teks_footer_tiket || "";
  const kode = tiket.kode_antrian || "";
  // Jam ambil ditampilkan dari `waktu_ambil` (ISO ber-offset WIB yang ditulis
  // server) — BUKAN dari jam perangkat. Tablet kasir yang jamnya meleset tidak
  // boleh mencetak jam yang berbeda dari yang tercatat di basis data.
  const jam = (tiket.waktu_ambil || "").slice(11, 16);

  const jalankanCetak = () => {
    cetak(areaRef.current);
    if (onSelesaiCetak) onSelesaiCetak();
  };

  return (
    <div className="w-full">
      <style>{`@media print { @page { size: ${lebar}mm auto; margin: 3mm; } }`}</style>

      {tampilkanTombolCetak && (
        <div className="no-print flex justify-end mb-3">
          <button
            type="button" onClick={jalankanCetak} data-testid="btn-cetak-tiket-antrian"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-slate-900 text-white text-sm hover:bg-slate-800"
          >
            <Printer className="w-4 h-4" /> Cetak Tiket
          </button>
        </div>
      )}

      <div
        ref={areaRef}
        data-testid="tiket-print-area"
        style={{ width: `${lebar}mm` }}
        className="print-area mx-auto bg-white border border-slate-200 rounded-xl shadow-sm p-3 font-mono text-[11px] leading-snug text-slate-900"
      >
        <p className="text-center font-bold text-[12px]" data-testid="tiket-cabang">
          {tiket.cabang || "-"}
        </p>
        <p className="text-center text-[10px] text-slate-600">{judul}</p>

        <div className="border-t border-dashed border-slate-400 my-1.5" />

        <p className="text-center font-bold leading-none" style={{ fontSize: "18mm" }}
           data-testid="tiket-nomor">
          {tiket.nomor_antrian || "-"}
        </p>

        <div className="border-t border-dashed border-slate-400 my-1.5" />

        <Barcode teks={kode} lebarMm={lebar - 12} />
        <p className="text-center text-[9px] break-all mt-1" data-testid="tiket-kode">{kode}</p>

        <div className="border-t border-dashed border-slate-400 my-1.5" />

        <div className="flex justify-between text-[10px]">
          <span>{tiket.tanggal || "-"}</span>
          <span>{jam}</span>
        </div>
        {antreanDiDepan !== null && (
          <p className="text-[10px] text-slate-600" data-testid="tiket-antrean-di-depan">
            Antrean di depan Anda: {antreanDiDepan}
          </p>
        )}
        {tiket.catatan ? <p className="text-[10px] text-slate-600">Catatan: {tiket.catatan}</p> : null}

        {footer ? (
          <>
            <div className="border-t border-dashed border-slate-400 my-1.5" />
            <p className="text-center text-[10px]">{footer}</p>
          </>
        ) : null}

        <p className="text-center text-[9px] text-slate-400 mt-1.5">
          Tunjukkan tiket ini ke petugas kami
        </p>
      </div>
    </div>
  );
}
